import React, { useState, useRef, useEffect } from 'react';
import { Header } from '../../components/header/Header';
import { MobileFooter } from '../../components/mobileFooter/MobileFooter';
import './BetaSignup.css';
import Plausible from 'plausible-tracker';
import { motion } from 'framer-motion';

const plausible = Plausible({
	domain: 'erledix.de',
});

const BetaSignupPage = () => {
	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [phone, setPhone] = useState('');
	const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

	const [errors, setErrors] = useState<{
		name?: string;
		email?: string;
		phone?: string;
		privacy?: string;
	}>({});

	const [success, setSuccess] = useState(false);
	const formRef = useRef<HTMLFormElement>(null);

	// Prüfen ob schon eingetragen (Cache via localStorage)
	useEffect(() => {
		if (localStorage.getItem('betaSignupSuccess') === 'true') {
			setSuccess(true);
		}
	}, []);

	function shakeForm() {
		if (formRef.current) {
			formRef.current.classList.remove('shake');
			void formRef.current.offsetWidth;
			formRef.current.classList.add('shake');
		}
	}

	function validate() {
		const newErrors: { name?: string; email?: string; phone?: string; privacy?: string } = {};

		if (!name.trim()) newErrors.name = 'Bitte gib deinen Namen an.';
		if (!email.trim()) newErrors.email = 'Bitte gib deine E-Mail-Adresse an.';
		else {
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (!emailRegex.test(email))
				newErrors.email = 'Bitte gib eine gültige E-Mail-Adresse an.';
		}

		if (phone.trim()) {
			const phoneRegex = /^[0-9+\s()-]{6,}$/;
			if (!phoneRegex.test(phone))
				newErrors.phone = 'Bitte gib eine gültige Telefonnummer an.';
		}

		if (!acceptedPrivacy) newErrors.privacy = 'Du musst der Datenschutzerklärung zustimmen.';

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	}

	function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();

		if (!validate()) {
			shakeForm();
			return;
		}

		const payload = { name, email, phone: phone.trim() || undefined };

		fetch('/scripts/betaSignup.php', {
			method: 'POST',
			body: JSON.stringify(payload),
			headers: { 'Content-Type': 'application/json' },
		})
			.then((response) => response.json())
			.then((data) => {
				if (data.success) {
					setSuccess(true);
					localStorage.setItem('betaSignupSuccess', 'true');
					plausible.trackEvent('betaSignup');
				} else {
					alert(data.errorMessage || 'Es gab ein Problem bei der Anmeldung.');
				}
			})
			.catch((error) => {
				console.error('Fehler bei der Anmeldung:', error);
				alert(
					'Es gab ein Problem bei der Anmeldung. Bitte versuche es später noch einmal.'
				);
			});
	}

	// Komponente für App-Download
	const AppDownloadSection = () => (
		<div className='app-download-section'>
			<h2>🚀 Die Beta ist jetzt verfügbar!</h2>
			<p>Lade dir die App jetzt herunter und teste sie selbst.</p>
			<div className='store-buttons'>
				<a
					href='https://apps.apple.com/de/app/erledix/id6755962859'
					target='_blank'
					rel='noreferrer'
					className='store-button app-store'
					onClick={() => plausible.trackEvent('appStoreDownload')}
				>
					<span className='store-icon'>🍎</span>
					<div className='store-text'>
						<small>Jetzt im</small>
						<strong>App Store</strong>
					</div>
				</a>
				<a
					href='https://play.google.com/store/apps/details?id=com.h365.erledix&pcampaignid=web_share'
					target='_blank'
					rel='noreferrer'
					className='store-button google-play'
					onClick={() => plausible.trackEvent('googlePlayDownload')}
				>
					<span className='store-icon'>🤖</span>
					<div className='store-text'>
						<small>Jetzt bei</small>
						<strong>Google Play</strong>
					</div>
				</a>
			</div>
		</div>
	);

	// Komponente für Feedback
	const FeedbackSection = () => (
		<div className='feedback-section'>
			<h2>💬 Dein Feedback ist wertvoll!</h2>
			<p>Hast du die App getestet? Wir würden uns über dein Feedback freuen!</p>
			<p className='feedback-subtext'>
				Deine Meinung hilft uns, Erledix noch besser zu machen.
			</p>
			<a
				href='https://docs.google.com/forms/d/e/1FAIpQLSdOf8NBGI2DuHzmGKswFFvbkxCS8tNzuSAHFNFgFHK0J7Yl_w/viewform?usp=header'
				target='_blank'
				rel='noreferrer'
				className='feedback-button'
			>
				📝 Zum Feedback-Formular
			</a>
		</div>
	);

	// Neue Komponente für Erfolg
	const SuccessMessage = () => (
		<div className='success-message'>
			<h2>🎉 Vielen Dank!</h2>
			<p>Du bist jetzt erfolgreich als Beta-Tester:in angemeldet.</p>
			<p>Wir melden uns bald mit weiteren Infos bei dir.</p>
		</div>
	);

	return (
		<div>
			<Header style={{ backgroundColor: '#ffffff' }} />
			<div className='beta-page-container'>
				<div className='beta-page-content'>
					<AppDownloadSection />
					<FeedbackSection />
					{success ? (
						<SuccessMessage />
					) : (
						<>
							<h1 className='beta-title'>
								Melde dich an um nichts zu verpassen!
							</h1>
							<p className='beta-description'>
								Sei von Anfang an dabei und forme mit uns die ToDo-App, die Familien
								wirklich hilft.
							</p>

							<form ref={formRef} onSubmit={handleSubmit} className='beta-form'>
								{/* Name */}
								<div className='form-group'>
									<label htmlFor='name'>Name*</label>
									<p className='form-info'>Für die persönliche Ansprache</p>
									<input
										type='text'
										id='name'
										placeholder='Dein Name'
										value={name}
										onChange={(e) => setName(e.target.value)}
										className={errors.name ? 'input-error' : ''}
									/>
									{errors.name && <p className='error-text'>{errors.name}</p>}
								</div>

								{/* Email */}
								<div className='form-group'>
									<label htmlFor='email'>E-Mail-Adresse*</label>
									<p className='form-info'>
										Mit deiner E-Mail Adresse erhälst du wichtige Informationen.
									</p>
									<input
										type='email'
										id='email'
										placeholder='deine@email.de'
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										className={errors.email ? 'input-error' : ''}
									/>
									{errors.email && <p className='error-text'>{errors.email}</p>}
								</div>

								{/* Phone */}
								<div className='form-group'>
									<label htmlFor='phone'>Telefonnummer (optional)</label>
									<p className='form-info'>
										Wir würden dich gerne anrufen, um schnellstmöglich Feedback
										zu erhalten, dafür benötigen wir deine Telefonnummer.
									</p>
									<input
										type='tel'
										id='phone'
										placeholder='+49 123 456789'
										value={phone}
										onChange={(e) => setPhone(e.target.value)}
										className={errors.phone ? 'input-error' : ''}
									/>
									{errors.phone && <p className='error-text'>{errors.phone}</p>}
								</div>

								{/* Privacy */}
								<div className='form-group'>
									<label className='form-group' style={{ fontWeight: 100 }}>
										<input
											type='checkbox'
											checked={acceptedPrivacy}
											onChange={(e) => setAcceptedPrivacy(e.target.checked)}
										/>
										Ich stimme der Verarbeitung meiner Daten gemäß der{' '}
										<a
											href='/datenschutz'
											target='_blank'
											rel='noreferrer'
											className='link-style'
										>
											Datenschutzerklärung
										</a>{' '}
										zu.
									</label>
									{errors.privacy && (
										<p className='error-text'>{errors.privacy}</p>
									)}
								</div>

								<button type='submit' className='submit-btn'>
									Jetzt anmelden &amp; mitgestalten
								</button>
							</form>
						</>
					)}
				</div>
			</div>
			<MobileFooter />
		</div>
	);
};

export default BetaSignupPage;
